import { toUint8Array } from 'js-base64'

import { slotPropertyId } from '@open-pencil/scene-graph'
import type { SceneNode, SceneGraph, Fill, Stroke } from '@open-pencil/scene-graph'
import type { RenderColorSpace, ResolvedRenderColor } from '@open-pencil/scene-graph/color'
import type { Color, Rect, Vector } from '@open-pencil/scene-graph/primitives'
import type { SnapGuide } from '@open-pencil/scene-graph/snap'

/* eslint-disable max-lines -- SkiaRenderer facade owns CanvasKit state and delegates domain drawing */
import {
  SELECTION_COLOR,
  COMPONENT_COLOR,
  SLOT_COLOR,
  CANVAS_BG_COLOR,
  DEFAULT_FONT_SIZE,
  COMPONENT_SET_DASH,
  COMPONENT_SET_DASH_GAP,
  COMPONENT_SET_BORDER_WIDTH,
  IS_BROWSER
} from '#core/constants'
import type { EditorState } from '#core/editor/types'
import { RenderProfiler } from '#core/profiler'
import type { TextEditor } from '#core/text/editor'
import type { FontResolutionSnapshot } from '#core/text/resolver'

import { createImageCache } from './images/cache'
import { ImagePreviewCache } from './images/previews'
import { LabelCache } from './labels/cache'
import * as LabelHitTest from './labels/hit-test'
import { LabelParagraphCache } from './labels/paragraph-cache'
import { labelHitOptions } from './labels/style'
import * as RenderColors from './renderer/colors'
import * as RendererFonts from './renderer/fonts.override'
import { destroyRenderer } from './renderer/lifecycle'
import { installRendererDomainMethods } from './renderer/methods'
import { initializeRendererPaints } from './renderer/paints'
import * as RenderPipeline from './renderer/pipeline'
import type { SceneBacking, SceneBackingBuild } from './renderer/retained-backing/types'
import * as RendererState from './renderer/state'
import * as RenderText from './text'
import { createGlyphSilhouetteCache } from './text/derived'
import { TextPreparationCache } from './text/preparation-cache'
export type { MeasurementMode, PresenceCursor, RenderOverlays, RulerTheme } from './renderer/types'
import type {
  Path,
  CanvasKit,
  Surface,
  Canvas,
  Paint,
  Font,
  FontMgr,
  TypefaceFontProvider,
  SkPicture,
  ImageFilter,
  MaskFilter,
  RuntimeEffect,
  Paragraph
} from 'canvaskit-wasm'

export interface SubtreePictureCacheEntry {
  picture: SkPicture
  pageId: string | null
  sceneVersion: number
  positionPreviewVersion: number
  fontGeneration: number
}

export interface PendingFontNode {
  node: SceneNode
  keys: Set<string>
}

import type { PlacedIssueMarker } from './issues/types'
import { EffectRasterCache } from './renderer/effect-raster-cache'
import { TiledSceneController } from './renderer/tiles'
import type { TransientCanvasPreview } from './renderer/transient-previews'
import type { PresenceCursor, RenderOverlays, RulerTheme } from './renderer/types'

export class SkiaRenderer {
  ck: CanvasKit
  surface: Surface
  declare fillPaint: Paint
  diamondGradientEffect: RuntimeEffect | null = null
  declare strokePaint: Paint
  declare selectionPaint: Paint
  declare parentOutlinePaint: Paint
  declare snapPaint: Paint
  declare auxFill: Paint
  declare auxStroke: Paint
  declare opacityPaint: Paint
  declare effectLayerPaint: Paint
  imageFilterCache = new Map<string, ImageFilter | null>()
  maskFilterCache = new Map<number, MaskFilter | null>()
  _tmpColor = new Float32Array(4)
  _tmpRect = new Float32Array(4)
  textFont: Font | null = null
  labelFont: Font | null = null
  sizeFont: Font | null = null
  sectionTitleFont: Font | null = null
  componentLabelFont: Font | null = null
  fontMgr: FontMgr | null = null
  fontProvider: TypefaceFontProvider | null = null
  fontsLoaded = false
  fontGeneration = 0
  onFontResolutionSettled:
    | ((snapshot: FontResolutionSnapshot, nodeIds: readonly string[]) => void)
    | undefined
  pendingFontNodes = new Map<string, PendingFontNode>()
  textPictureGenerations = new Map<string, { data: Uint8Array; generation: number }>()
  readonly transientPreviews = new Map<string, TransientCanvasPreview>()
  imageCache = createImageCache()
  viewportImageRendering = false
  imageMemoryGraph: SceneGraph | null = null
  imageMemoryPage: string | null = null
  onImagePreviewReady: (() => void) | null = null
  readonly imagePreviews = new ImagePreviewCache(() => this.onImagePreviewReady?.())
  vectorPathCache = new Map<string, Path[]>()
  vectorStrokePathCache = new Map<string, Path[]>()
  vectorStrokeOutlineCache = new Map<string, Path[]>()
  fillGeometryCache = new Map<string, Path[]>()
  strokeGeometryCache = new Map<string, Path[]>()
  /** Path-text glyph silhouettes (stroke-and-union, font units) keyed by blob hash + relative weight. */
  glyphSilhouetteCache = createGlyphSilhouetteCache()
  renderingSceneBacking = false
  scenePicture: SkPicture | null = null
  scenePictureVersion = -1
  scenePictureFontGeneration = -1
  scenePicturePositionPreviewVersion = -1
  scenePicturePageId: string | null = null
  sceneBacking: SceneBacking | null = null
  sceneBackingPreviewUntil = 0
  sceneBackingNeedsCrispRender = false
  sceneBackingAllocationFailed = false
  sceneBackingBuild: SceneBackingBuild | null = null
  sceneBackingAverageRecordMs = 40
  sceneBackingAverageViewportIntervalMs = 80
  sceneBackingLastViewportEventAt = 0
  navigationPhase: EditorState['navigation']['phase'] = 'idle'
  navigationGeneration = 0
  tiledSceneEnabled = false
  /** Prefer larger contiguous tile fills while the canvas loading overlay is visible. */
  tiledSceneLoadingBoost = false
  tracksSceneSettlement = true
  /** Colour space this renderer's surface presents; colours convert into it when painting. */
  presentationColorSpace: RenderColorSpace = 'srgb'
  tiledScenePending = false
  tiledSceneCovered = false
  lastSceneViewport: { panX: number; panY: number; zoom: number } | null = null
  nodePictureCache = new Map<string, SkPicture | null>()
  nodePictureCacheGenerations = new Map<string, number>()
  nodePictureCacheDependencies = new Map<string, readonly string[]>()
  effectRasterCache = new EffectRasterCache()
  subtreePictureCache = new Map<string, SubtreePictureCacheEntry>()
  subtreePictureCachePageId: string | null = null
  subtreePictureCacheSceneVersion = -1
  subtreePictureCachePositionPreviewVersion = -1
  subtreePictureCacheFontGeneration = -1
  readonly labelCache = new LabelCache()
  readonly labelParagraphCache = new LabelParagraphCache(undefined, undefined, {
    onMissingGlyphs: (missing) => RendererFonts.resolveLabelFontCoverage(this, missing)
  })
  readonly textPreparationCache = new TextPreparationCache()
  readonly tiledScene = new TiledSceneController()
  readonly profiler: RenderProfiler

  declare rulerBgPaint: Paint
  declare rulerTickPaint: Paint
  declare rulerTextPaint: Paint
  declare rulerHlPaint: Paint
  declare rulerBadgePaint: Paint
  declare rulerLabelPaint: Paint
  declare penPathPaint: Paint
  declare penLiveStrokePaint: Paint
  declare penHandlePaint: Paint
  declare penVertexFill: Paint
  declare penVertexStroke: Paint

  panX = 0
  panY = 0
  zoom = 1
  dpr = 1
  viewportWidth = 0
  viewportHeight = 0
  showRulers = true
  pageColor = CANVAS_BG_COLOR
  rulerTheme: RulerTheme | null = null
  pageId: string | null = null
  /** Issue markers placed in the last overlay pass; hit testing reads the same layout. */
  issueMarkers: PlacedIssueMarker[] = []
  /** Screen rectangles of UI floating over this canvas, which overlays such as edge pins avoid. */
  overlayObstacles: readonly Rect[] = []

  boundEffectLayersToViewport = false
  worldViewport = { x: 0, y: 0, w: 0, h: 0 }
  _nodeCount = 0
  _culledCount = 0
  _flashes: Array<{ nodeId: string; startTime: number }> = []
  _flashPaint: Paint | null = null
  _aiActiveNodes: Set<string> = new Set()
  _aiDoneFlashes: Array<{ nodeId: string; startTime: number }> = []

  readonly DEFAULT_FONT_SIZE = DEFAULT_FONT_SIZE
  readonly COMPONENT_SET_BORDER_WIDTH = COMPONENT_SET_BORDER_WIDTH
  readonly COMPONENT_SET_DASH = COMPONENT_SET_DASH
  readonly COMPONENT_SET_DASH_GAP = COMPONENT_SET_DASH_GAP

  declare drawHoverHighlight: (
    canvas: Canvas,
    graph: SceneGraph,
    hoveredNodeId?: string | null,
    preview?: RenderOverlays['rotationPreview']
  ) => void
  declare drawMeasurements: (
    canvas: Canvas,
    graph: SceneGraph,
    selectedIds: Set<string>,
    targetId?: string | null
  ) => void
  declare drawEnteredContainer: (
    canvas: Canvas,
    graph: SceneGraph,
    enteredContainerId?: string | null,
    preview?: RenderOverlays['rotationPreview']
  ) => void
  declare drawSelection: (
    canvas: Canvas,
    graph: SceneGraph,
    selectedIds: Set<string>,
    overlays: RenderOverlays
  ) => void
  declare drawNodeSelection: (
    canvas: Canvas,
    node: SceneNode,
    rotation: number,
    graph: SceneGraph,
    preview?: RenderOverlays['rotationPreview']
  ) => void
  declare drawSelectionLabels: (
    canvas: Canvas,
    graph: SceneGraph,
    selectedIds: Set<string>,
    overlays?: RenderOverlays
  ) => void
  declare drawParentFrameOutlines: (
    canvas: Canvas,
    graph: SceneGraph,
    selectedIds: Set<string>,
    preview?: RenderOverlays['rotationPreview']
  ) => void
  declare drawNodeOutline: (
    canvas: Canvas,
    node: SceneNode,
    rotation: number,
    graph: SceneGraph,
    preview?: RenderOverlays['rotationPreview']
  ) => void
  declare drawGroupBounds: (
    canvas: Canvas,
    nodes: SceneNode[],
    graph: SceneGraph,
    preview?: RenderOverlays['rotationPreview']
  ) => void
  declare getRotatedCorners: (node: SceneNode, abs: Vector) => Vector[]
  declare drawHandle: (canvas: Canvas, x: number, y: number) => void
  declare drawSnapGuides: (canvas: Canvas, guides?: SnapGuide[]) => void
  declare drawMarquee: (canvas: Canvas, marquee?: Rect | null) => void
  declare drawFlashes: (canvas: Canvas, graph: SceneGraph) => void
  declare drawLayoutInsertIndicator: (
    canvas: Canvas,
    indicator?: RenderOverlays['layoutInsertIndicator']
  ) => void
  declare drawAutoLayoutHover: (
    canvas: Canvas,
    graph: SceneGraph,
    hover?: RenderOverlays['autoLayoutHover']
  ) => void
  declare drawTextEditOverlay: (canvas: Canvas, node: SceneNode, editor: TextEditor) => void
  declare drawNodeEditOverlay: (
    canvas: Canvas,
    graph: SceneGraph,
    editState?: RenderOverlays['nodeEditState']
  ) => void
  declare drawPenOverlay: (canvas: Canvas, penState: RenderOverlays['penState']) => void
  declare drawPresenceCursors: (
    canvas: Canvas,
    graph: SceneGraph,
    cursors?: PresenceCursor[]
  ) => void
  declare drawRulers: (
    canvas: Canvas,
    graph: SceneGraph,
    selectedIds: Set<string>,
    guides?: RenderOverlays['guides']
  ) => void
  declare drawFrameTitles: (
    canvas: Canvas,
    graph: SceneGraph,
    selectedIds: ReadonlySet<string>,
    overlays?: RenderOverlays
  ) => void
  declare drawSectionTitles: (canvas: Canvas, graph: SceneGraph, overlays?: RenderOverlays) => void
  declare drawComponentLabels: (
    canvas: Canvas,
    graph: SceneGraph,
    overlays?: RenderOverlays
  ) => void
  declare renderNodeSelf: (
    canvas: Canvas,
    graph: SceneGraph,
    nodeId: string,
    overlays?: RenderOverlays
  ) => void
  declare renderNode: (
    canvas: Canvas,
    graph: SceneGraph,
    nodeId: string,
    overlays: RenderOverlays,
    parentAbsX?: number,
    parentAbsY?: number,
    hasTransformedAncestor?: boolean
  ) => void
  declare renderSection: (canvas: Canvas, node: SceneNode, graph: SceneGraph) => void
  declare renderComponentSet: (canvas: Canvas, node: SceneNode, graph: SceneGraph) => void
  declare renderShape: (canvas: Canvas, node: SceneNode, graph: SceneGraph) => void
  declare renderShapeUncached: (canvas: Canvas, node: SceneNode, graph: SceneGraph) => void
  declare renderEffects: (
    canvas: Canvas,
    node: SceneNode,
    rect: Float32Array,
    hasRadius: boolean,
    pass: 'behind' | 'front',
    shadowShapeChild?: SceneNode | null
  ) => void
  declare renderText: (canvas: Canvas, node: SceneNode, fill?: Fill) => void
  declare drawNodeFill: (
    canvas: Canvas,
    node: SceneNode,
    rect: Float32Array,
    hasRadius: boolean,
    fill?: Fill
  ) => void
  declare applyFill: (fill: Fill, node: SceneNode, graph: SceneGraph, fillIndex?: number) => boolean
  declare applyGradientFill: (fill: Fill, node: SceneNode, graph: SceneGraph) => void
  declare applyImageFill: (fill: Fill, node: SceneNode, graph: SceneGraph) => boolean
  declare drawArc: (canvas: Canvas, node: SceneNode, paint: Paint) => void
  declare drawNodeStroke: (
    canvas: Canvas,
    node: SceneNode,
    rect: Float32Array,
    hasRadius: boolean
  ) => void
  declare drawStrokeWithAlign: (
    canvas: Canvas,
    node: SceneNode,
    rect: Float32Array,
    hasRadius: boolean,
    align: 'INSIDE' | 'CENTER' | 'OUTSIDE'
  ) => void
  declare drawRRectStrokeWithAlign: (
    canvas: Canvas,
    rrect: Float32Array,
    node: SceneNode,
    stroke: Stroke
  ) => void
  declare drawIndividualSideStrokes: (
    canvas: Canvas,
    node: SceneNode,
    align: 'INSIDE' | 'CENTER' | 'OUTSIDE'
  ) => void
  declare strokeNodeShape: (canvas: Canvas, node: SceneNode, paint: Paint) => void
  declare makeNodeShapePath: (node: SceneNode, rect: Float32Array, hasRadius: boolean) => Path
  declare makePolygonPath: (node: SceneNode) => Path
  declare makeRRect: (node: SceneNode) => Float32Array
  declare makeRRectWithSpread: (node: SceneNode, spread: number) => Float32Array
  declare makeRRectWithOffset: (
    node: SceneNode,
    ox: number,
    oy: number,
    spread: number
  ) => Float32Array
  declare clipNodeShape: (
    canvas: Canvas,
    node: SceneNode,
    rect: Float32Array,
    hasRadius: boolean
  ) => void
  declare getVectorPaths: (node: SceneNode) => Path[] | null
  declare getFillGeometry: (node: SceneNode) => Path[] | null
  declare getStrokeGeometry: (node: SceneNode) => Path[] | null
  declare getCachedDropShadow: (
    dx: number,
    dy: number,
    sigma: number,
    color: Float32Array
  ) => ImageFilter
  declare getCachedBlur: (sigma: number) => ImageFilter
  declare getCachedDecalBlur: (sigma: number) => ImageFilter
  declare getCachedMaskBlur: (sigma: number) => MaskFilter
  declare applyClippedBlur: (
    canvas: Canvas,
    node: SceneNode,
    rect: Float32Array,
    hasRadius: boolean,
    sigma: number
  ) => void
  color4f(r: number, g: number, b: number, a: number): Float32Array {
    const c = this._tmpColor
    c[0] = r
    c[1] = g
    c[2] = b
    c[3] = a
    return c
  }

  ltrb(l: number, t: number, r: number, b: number): Float32Array {
    const rc = this._tmpRect
    rc[0] = l
    rc[1] = t
    rc[2] = r
    rc[3] = b
    return rc
  }

  selColor(alpha = 1) {
    return this.ck.Color4f(SELECTION_COLOR.r, SELECTION_COLOR.g, SELECTION_COLOR.b, alpha)
  }

  compColor(alpha = 1) {
    return this.ck.Color4f(COMPONENT_COLOR.r, COMPONENT_COLOR.g, COMPONENT_COLOR.b, alpha)
  }

  slotColor(alpha = 1) {
    return this.ck.Color4f(SLOT_COLOR.r, SLOT_COLOR.g, SLOT_COLOR.b, alpha)
  }

  /** The outline colour for a node: pink for slots, purple for components, blue otherwise. */
  outlineColor(node: SceneNode) {
    if (slotPropertyId(node)) return this.slotColor()
    return this.isComponentType(node.type) ? this.compColor() : this.selColor()
  }

  isComponentType(type: string): boolean {
    return type === 'COMPONENT' || type === 'COMPONENT_SET' || type === 'INSTANCE'
  }

  isRectangularType(type: string): boolean {
    return (
      type === 'FRAME' ||
      type === 'RECTANGLE' ||
      type === 'ROUNDED_RECTANGLE' ||
      type === 'COMPONENT' ||
      type === 'INSTANCE' ||
      type === 'SECTION' ||
      type === 'GROUP'
    )
  }

  effectOverflow(node: SceneNode): number {
    let expand = 0
    for (const e of node.effects) {
      if (!e.visible) continue
      const blur = e.radius
      const spread = e.spread
      const ox = Math.abs(e.offset.x)
      const oy = Math.abs(e.offset.y)
      expand = Math.max(expand, blur + spread + ox, blur + spread + oy)
    }
    return expand
  }

  constructor(ck: CanvasKit, surface: Surface, gl?: WebGL2RenderingContext | null) {
    this.ck = ck
    this.surface = surface
    this.profiler = new RenderProfiler(ck, gl ?? null)
    initializeRendererPaints(this)
  }

  getFontProvider(): TypefaceFontProvider | null {
    return RendererFonts.getFontProvider(this)
  }

  isDestroyed(): boolean {
    return this.destroyed
  }

  async loadFonts(onFallbackFontsLoaded?: () => void): Promise<void> {
    await RendererFonts.loadFonts(this, onFallbackFontsLoaded)
  }

  syncFontGeneration(): void {
    RendererFonts.syncFontGeneration(this)
  }

  trackFontDemand(node: SceneNode, key: string): void {
    RendererFonts.trackFontDemand(this, node, key)
  }

  isTextPictureCurrent(node: SceneNode): boolean {
    return RendererFonts.isTextPictureCurrent(this, node)
  }

  async prepareForExport(graph: SceneGraph, pageId: string, nodeIds: string[]): Promise<void> {
    return RendererFonts.prepareForExport(this, graph, pageId, nodeIds)
  }

  replaceSurface(surface: Surface): void {
    this.tiledScene.destroy()
    this.surface.delete()
    this.surface = surface
    this.sceneBackingAllocationFailed = false
    this.invalidateScenePicture()
  }

  invalidateScenePicture(): void {
    RendererState.invalidateScenePicture(this)
  }

  invalidateAllPictures(): void {
    RendererState.invalidateAllPictures(this)
  }

  /** Drops `nodeId`'s cached drawing; `changedKeys`, when known, lets text keep its glyph coverage. */
  invalidateNodePicture(nodeId: string, changedKeys?: readonly (keyof SceneNode)[]): void {
    RendererState.invalidateNodePicture(this, nodeId, changedKeys)
  }

  flashNode(nodeId: string): void {
    RendererState.flashNode(this, nodeId)
  }

  aiMarkActive(nodeIds: string[]): void {
    RendererState.aiMarkActive(this, nodeIds)
  }

  aiMarkDone(nodeIds: string[]): void {
    RendererState.aiMarkDone(this, nodeIds)
  }

  aiFlashDone(nodeIds: string[]): void {
    RendererState.aiFlashDone(this, nodeIds)
  }

  aiClearActive(): void {
    RendererState.aiClearActive(this)
  }

  aiClearAll(): void {
    RendererState.aiClearAll(this)
  }

  get hasActiveFlashes(): boolean {
    return RendererState.hasActiveFlashes(this)
  }

  hitTestSectionTitle(
    graph: SceneGraph,
    canvasX: number,
    canvasY: number,
    preview?: RenderOverlays['rotationPreview']
  ): SceneNode | null {
    return LabelHitTest.hitTestSectionTitle(
      graph,
      canvasX,
      canvasY,
      this.zoom,
      this.pageId ?? graph.rootId,
      this.sectionTitleFont,
      this.labelCache,
      labelHitOptions(this, graph, preview)
    )
  }

  hitTestComponentLabel(
    graph: SceneGraph,
    canvasX: number,
    canvasY: number,
    preview?: RenderOverlays['rotationPreview']
  ): SceneNode | null {
    return LabelHitTest.hitTestComponentLabel(
      graph,
      canvasX,
      canvasY,
      this.zoom,
      this.pageId ?? graph.rootId,
      this.componentLabelFont,
      this.labelCache,
      labelHitOptions(this, graph, preview)
    )
  }

  hitTestFrameTitle(
    graph: SceneGraph,
    canvasX: number,
    canvasY: number,
    preview?: RenderOverlays['rotationPreview']
  ): SceneNode | null {
    return LabelHitTest.hitTestFrameTitle(
      graph,
      canvasX,
      canvasY,
      this.zoom,
      this.pageId ?? graph.rootId,
      this.labelFont,
      this.labelCache,
      labelHitOptions(this, graph, preview)
    )
  }

  renderSceneToCanvas(canvas: Canvas, graph: SceneGraph, pageId: string): void {
    RenderPipeline.renderSceneToCanvas(this, canvas, graph, pageId)
  }

  renderFromEditorState(
    state: EditorState,
    graph: SceneGraph,
    textEditor: unknown,
    viewportWidth: number,
    viewportHeight: number,
    showRulers = true,
    layer: RenderPipeline.RenderLayer = 'full',
    interactive = false
  ): void {
    const dpr = IS_BROWSER ? window.devicePixelRatio || 1 : 1
    RenderPipeline.renderFromEditorState(
      this,
      state,
      graph,
      textEditor,
      viewportWidth,
      viewportHeight,
      showRulers,
      dpr,
      layer,
      interactive
    )
  }

  render(
    graph: SceneGraph,
    selectedIds: Set<string>,
    overlays: RenderOverlays = {},
    sceneVersion = -1,
    layer: RenderPipeline.RenderLayer = 'full'
  ): void {
    RenderPipeline.render(this, graph, selectedIds, overlays, sceneVersion, layer)
  }

  invalidateVectorPath(nodeId: string): void {
    for (const cache of [this.vectorPathCache, this.vectorStrokePathCache]) {
      const old = cache.get(nodeId)
      if (!old) continue
      for (const p of old) p.delete()
      cache.delete(nodeId)
    }
    for (const [key, paths] of this.vectorStrokeOutlineCache) {
      if (!key.startsWith(`${nodeId}|`)) continue
      for (const p of paths) p.delete()
      this.vectorStrokeOutlineCache.delete(key)
    }
    for (const cache of [this.fillGeometryCache, this.strokeGeometryCache]) {
      const oldGeom = cache.get(nodeId)
      if (oldGeom) {
        for (const p of oldGeom) p.delete()
        cache.delete(nodeId)
      }
    }
  }

  measureTextNode(node: SceneNode, maxWidth?: number): { width: number; height: number } | null {
    return RenderText.measureTextNode(this, node, maxWidth)
  }

  nodeFontReadiness(node: SceneNode): RenderText.NodeFontReadiness {
    return RenderText.nodeFontReadiness(this, node)
  }

  isNodeFontLoaded(node: SceneNode): boolean {
    return RenderText.isNodeFontLoaded(this, node)
  }

  buildTextPicture(node: SceneNode): Uint8Array | null {
    return RenderText.buildTextPicture(this, node)
  }

  buildParagraph(
    node: SceneNode,
    color?: Float32Array,
    opts?: RenderText.ParagraphBuildOptions
  ): Paragraph {
    return RenderText.buildParagraph(this, node, color, opts)
  }

  resolveFillColorInfo(
    fill: Fill,
    fillIndex: number,
    node: SceneNode,
    graph: SceneGraph
  ): ResolvedRenderColor {
    return RenderColors.resolveFillColorInfo(
      fill,
      fillIndex,
      node,
      graph,
      this.presentationColorSpace
    )
  }

  resolveFillColor(fill: Fill, fillIndex: number, node: SceneNode, graph: SceneGraph): Color {
    return RenderColors.resolveFillColor(fill, fillIndex, node, graph, this.presentationColorSpace)
  }

  resolveStrokeColorInfo(
    stroke: Stroke,
    strokeIndex: number,
    node: SceneNode,
    graph: SceneGraph
  ): ResolvedRenderColor {
    return RenderColors.resolveStrokeColorInfo(
      stroke,
      strokeIndex,
      node,
      graph,
      this.presentationColorSpace
    )
  }

  resolveStrokeColor(
    stroke: Stroke,
    strokeIndex: number,
    node: SceneNode,
    graph: SceneGraph
  ): Color {
    return RenderColors.resolveStrokeColor(
      stroke,
      strokeIndex,
      node,
      graph,
      this.presentationColorSpace
    )
  }

  screenToCanvas(sx: number, sy: number): Vector {
    return {
      x: (sx - this.panX) / this.zoom,
      y: (sy - this.panY) / this.zoom
    }
  }

  /**
   * Browser fallback for raster formats CanvasKit cannot encode itself
   * (this build returns null from `encodeToBytes` for JPEG/WEBP). Routes the
   * RGBA pixels through an HTMLCanvasElement so `toDataURL` does the encoding.
   * Returns null outside the browser or if encoding fails.
   */
  encodeRasterFallback(
    pixels: Uint8Array,
    width: number,
    height: number,
    format: 'JPG' | 'WEBP',
    quality: number
  ): Uint8Array | null {
    if (!IS_BROWSER) return null
    try {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      const imageData = ctx.createImageData(width, height)
      imageData.data.set(pixels)
      ctx.putImageData(imageData, 0, 0)
      const mime = format === 'JPG' ? 'image/jpeg' : 'image/webp'
      const dataURL = canvas.toDataURL(mime, quality / 100)
      // Browsers silently return a PNG data URL when the requested encoder is
      // unsupported; reject that so we never write PNG bytes under a .jpg/.webp file.
      if (!dataURL.startsWith(`data:${mime}`)) return null
      const base64 = dataURL.split(',')[1]
      if (!base64) return null
      return toUint8Array(base64)
    } catch (err) {
      console.warn('Raster encode fallback failed:', err)
      return null
    }
  }

  destroyed: boolean = false

  destroy(): void {
    destroyRenderer(this)
  }
}

installRendererDomainMethods(SkiaRenderer.prototype)
